// src/store/actions/attestationActions.js
import { ATTESTATION_ACTIONS } from './actionTypes';
import attestationService from '../../services/attestationService';

// Fetch attestations
export const fetchAttestations = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_REQUEST });

    const response = await attestationService.getAttestations(params);

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_FAILURE,
      payload: error.message || 'Failed to fetch attestations',
    });
  }
};

// Fetch single attestation
export const fetchAttestation = (id) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_REQUEST });

    const response = await attestationService.getAttestationById(id);

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_FAILURE,
      payload: error.message || 'Failed to fetch attestation',
    });
    throw error;
  }
};

// Create attestation
export const createAttestation = (attestationData) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.CREATE_ATTESTATION_REQUEST });

    const response = await attestationService.createAttestation(attestationData);

    dispatch({
      type: ATTESTATION_ACTIONS.CREATE_ATTESTATION_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.CREATE_ATTESTATION_FAILURE,
      payload: error.message || 'Failed to create attestation',
    });
    throw error;
  }
};

// Update attestation
export const updateAttestation = (id, attestationData) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_REQUEST });

    const response = await attestationService.updateAttestation(id, attestationData);

    dispatch({
      type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_FAILURE,
      payload: error.message || 'Failed to update attestation',
    });
    throw error;
  }
};

// Delete attestation
export const deleteAttestation = (id) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_REQUEST });

    const response = await attestationService.deleteAttestation(id);

    dispatch({
      type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_SUCCESS,
      payload: { id, message: response.message },
    });

    return response;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_FAILURE,
      payload: error.message || 'Failed to delete attestation',
    });
    throw error;
  }
};

// Update attestation status
export const updateAttestationStatus = (id, status) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_REQUEST });

    const response = await attestationService.updateAttestationStatus(id, status);

    dispatch({
      type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_FAILURE,
      payload: error.message || 'Failed to update attestation status',
    });
    throw error;
  }
};

// Sync attestation
export const syncAttestation = (id) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.SYNC_ATTESTATION_REQUEST });

    const response = await attestationService.syncAttestation(id);

    dispatch({
      type: ATTESTATION_ACTIONS.SYNC_ATTESTATION_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.SYNC_ATTESTATION_FAILURE,
      payload: error.message || 'Failed to sync attestation',
    });
    throw error;
  }
};

// Bulk sync attestations
export const bulkSyncAttestations = (ids) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_REQUEST });

    const response = await attestationService.bulkSyncAttestations(ids);

    dispatch({
      type: ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_SUCCESS,
      payload: response,
    });

    return response;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_FAILURE,
      payload: error.message || 'Failed to bulk sync attestations',
    });
    throw error;
  }
};

// Fetch attestation stats
export const fetchAttestationStats = () => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_REQUEST });

    const response = await attestationService.getAttestationStats();

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_FAILURE,
      payload: error.message || 'Failed to fetch attestation stats',
    });
  }
};

// Fetch attestations by sender
export const fetchAttestationsBySender = (senderId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_REQUEST });

    const response = await attestationService.getAttestationsBySender(senderId, params);

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_FAILURE,
      payload: error.message || 'Failed to fetch attestations by sender',
    });
  }
};

// Fetch attestations by receiver
export const fetchAttestationsByReceiver = (receiverId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_REQUEST });

    const response = await attestationService.getAttestationsByReceiver(receiverId, params);

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_FAILURE,
      payload: error.message || 'Failed to fetch attestations by receiver',
    });
  }
};

// Export attestations
export const exportAttestations = (format = 'csv', filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_REQUEST });

    const response = await attestationService.exportAttestations(format, filters);

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `attestations.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    dispatch({
      type: ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_FAILURE,
      payload: error.message || 'Failed to export attestations',
    });
  }
};

// Fetch attestation documents
export const fetchAttestationDocuments = (attestationId, params = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_REQUEST });

    const response = await attestationService.getAttestationDocuments(attestationId, params);

    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_FAILURE,
      payload: error.message || 'Failed to fetch documents',
    });
  }
};

// Upload attestation document

export const uploadAttestationDocument = (attestationId, file, documentData, onProgress) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_REQUEST });

    // You can track progress via the onProgress callback
    const response = await attestationService.uploadAttestationDocument(
      attestationId,
      file,
      documentData
    );

    dispatch({
      type: ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_FAILURE,
      payload: error.message || 'Failed to upload document',
    });
    throw error;
  }
};

// src/store/actions/attestationActions.js

// Request presigned upload URL
export const requestPresignedUpload = (attestationId, fileData) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.REQUEST_PRESIGNED_UPLOAD_REQUEST });

    const response = await attestationService.requestPresignedUpload(attestationId, fileData);

    dispatch({
      type: ATTESTATION_ACTIONS.REQUEST_PRESIGNED_UPLOAD_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.REQUEST_PRESIGNED_UPLOAD_FAILURE,
      payload: error.message || 'Failed to request upload URL',
    });
    throw error;
  }
};

// Confirm document upload
export const confirmDocumentUpload = (attestationId, uploadData) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.CONFIRM_DOCUMENT_UPLOAD_REQUEST });

    const response = await attestationService.confirmDocumentUpload(attestationId, uploadData);

    dispatch({
      type: ATTESTATION_ACTIONS.CONFIRM_DOCUMENT_UPLOAD_SUCCESS,
      payload: response.data,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.CONFIRM_DOCUMENT_UPLOAD_FAILURE,
      payload: error.message || 'Failed to confirm upload',
    });
    throw error;
  }
};

// Get presigned download URL
export const getPresignedDownloadUrl = (attestationId, documentId) => async (dispatch) => {
  try {
    const response = await attestationService.getPresignedDownloadUrl(attestationId, documentId);
    return response.data;
  } catch (error) {
    console.error('Failed to get download URL:', error);
    throw error;
  }
};



// Delete attestation document
export const deleteAttestationDocument = (attestationId, documentId) => async (dispatch) => {
  try {
    dispatch({ type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_REQUEST });

    const response = await attestationService.deleteAttestationDocument(attestationId, documentId);

    dispatch({
      type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_SUCCESS,
      payload: { documentId, message: response.message },
    });

    return response;
  } catch (error) {
    dispatch({
      type: ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_FAILURE,
      payload: error.message || 'Failed to delete document',
    });
    throw error;
  }
};

// Clear document error
export const clearDocumentError = () => ({
  type: ATTESTATION_ACTIONS.CLEAR_DOCUMENT_ERROR,
});

// Clear document success
export const clearDocumentSuccess = () => ({
  type: ATTESTATION_ACTIONS.CLEAR_DOCUMENT_SUCCESS,
});

// Set attestation filters
export const setAttestationFilters = (filters) => ({
  type: ATTESTATION_ACTIONS.SET_ATTESTATION_FILTERS,
  payload: filters,
});

// Clear error
export const clearAttestationError = () => ({
  type: ATTESTATION_ACTIONS.CLEAR_ATTESTATION_ERROR,
});

// Clear success
export const clearAttestationSuccess = () => ({
  type: ATTESTATION_ACTIONS.CLEAR_ATTESTATION_SUCCESS,
});

// Clear selected attestation
export const clearSelectedAttestation = () => ({
  type: ATTESTATION_ACTIONS.CLEAR_SELECTED_ATTESTATION,
});